<script setup lang="ts">
/**
 * Global reward-feedback host (M4C-29).
 *
 * Mounted once in the member layout. Reads the ephemeral reward queue
 * (`useRewardFeedbackStore`) and renders the two surfaces it feeds:
 *   • the star-born modal for the current `star_born` event;
 *   • a stack of activity toasts for `fragments_gained` / `milestone` events,
 *     teleported to a fixed bottom region (full-width on mobile, a 360px column
 *     bottom-right on desktop) so they never fight the page layout.
 *
 * Callers request feedback through `useGamificationService().emitRewardFeedback()`
 * — they never touch the modal/toast internals. A `?reward=` query param drives a
 * mock trigger for development/demo (`star` | `toasts` | `all`) until live action
 * responses exist (M4C-35); it's stripped from the URL after firing.
 */
import { useGamificationService } from '@/composables/gamification';
import { useRewardFeedbackStore } from '@/stores/rewardFeedback';
import type { MockRewardScenario } from '@/composables/gamification/service';

const store = useRewardFeedbackStore();
const service = useGamificationService();
const route = useRoute();
const router = useRouter();

/** Map the `?reward=` value to a demo scenario (null = not a trigger). */
function toScenario(value: unknown): MockRewardScenario | null {
  const v = Array.isArray(value) ? value[0] : value;
  if (v === undefined || v === null) {
    return null;
  }
  const key = String(v).toLowerCase();
  if (key === 'star' || key === 'modal') {
    return 'star';
  }
  if (key === 'toast' || key === 'toasts') {
    return 'toasts';
  }
  return 'all'; // present but unrecognised → fire everything
}

watch(
  () => route.query.reward,
  (value) => {
    if (!import.meta.client) {
      return;
    }
    const scenario = toScenario(value);
    if (!scenario) {
      return;
    }
    service.triggerMockRewardFeedback(scenario);
    // Strip the trigger so it doesn't re-fire and the URL stays clean.
    const { reward, ...rest } = route.query;
    void router.replace({ query: rest });
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <GamiStarBornModal
      v-if="store.modalEvent"
      :key="store.modalEvent.id"
      :event="store.modalEvent"
      @close="store.dismissModal()"
    />

    <Teleport to="body">
      <TransitionGroup
        tag="div"
        class="pointer-events-none fixed inset-x-0 bottom-0 z-[1090] flex flex-col gap-3 p-4 md:inset-x-auto md:right-0 md:w-[392px]"
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="translate-y-2 opacity-0"
        leave-active-class="absolute transition-all duration-200 ease-in"
        leave-to-class="translate-y-2 opacity-0"
        move-class="transition-transform duration-200"
      >
        <GamiActivityToast
          v-for="toast in store.toasts"
          :key="toast.id"
          :event="toast"
          class="pointer-events-auto"
          @dismiss="store.dismiss(toast.id)"
        />
      </TransitionGroup>
    </Teleport>
  </div>
</template>
